import { useEffect, useState } from 'react';

import { loadReviewPhotoVariantObjectUrl } from '../utils/reviewPhotoStorage';

/**
 * Loads a review-photo variant into a blob object URL (revoked on cleanup).
 * @param {object} params
 * @param {'beer' | 'hospitality'} params.reviewType
 * @param {string} params.reviewId
 * @param {string} params.photoId
 * @param {'thumb' | 'display'} params.variantName
 * @param {boolean} [params.enabled]
 * @returns {{ url: string | null, loading: boolean, error: boolean }}
 */
function useReviewPhotoVariantUrl({
  reviewType,
  reviewId,
  photoId,
  variantName,
  enabled = true,
}) {
  const loadKey = enabled && reviewType && reviewId && photoId && variantName
    ? `${reviewType}/${reviewId}/${photoId}/${variantName}`
    : '';
  const [result, setResult] = useState({
    loadKey: '',
    url: null,
    error: false,
  });

  useEffect(() => {
    if (!loadKey) {
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    loadReviewPhotoVariantObjectUrl(reviewType, reviewId, photoId, variantName)
      .then((loadedUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(loadedUrl);
          return;
        }
        objectUrl = loadedUrl;
        setResult({
          loadKey,
          url: loadedUrl,
          error: false,
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setResult({
          loadKey,
          url: null,
          error: true,
        });
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [loadKey, reviewType, reviewId, photoId, variantName]);

  if (!loadKey) {
    return { url: null, loading: false, error: false };
  }

  const isCurrentResult = result.loadKey === loadKey;
  return {
    url: isCurrentResult ? result.url : null,
    loading: !isCurrentResult,
    error: isCurrentResult ? result.error : false,
  };
}

export default useReviewPhotoVariantUrl;
